import React, { useMemo } from "react";
import client from "@culturehq/client";
import { Loader, Pagination, Table } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, usePagination, useSortable } from "app/hooks";
import { t, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import Anchor from "components/display/Anchor";
import PageHeader from "components/display/PageHeader";

import LeaderboardWidget from "./LeaderboardWidget";
import NoWidgets from "./NoWidgets";
import RecognitionWidget from "./RecognitionWidget";
import SurveyWidget from "./SurveyWidget";
import WidgetRow from "./WidgetRow";
import StartTrendWidget from "./StartTrendWidget";

const getWidgets = (announcements, organization) => {
  const { widgetOrder = {} } = organization;
  const widgets = [...announcements];

  if (organization.recognitionsEnabled && typeof widgetOrder.recognition === "number") {
    widgets.push({ id: "recognition", position: widgetOrder.recognition });
  }

  if (organization.surveysEnabled && typeof widgetOrder.survey === "number") {
    widgets.push({ id: "survey", position: widgetOrder.survey });
  }

  if (organization.gamificationEnabled && typeof widgetOrder.leaderboard === "number") {
    widgets.push({ id: "leaderboard", position: widgetOrder.leaderboard });
  }

  widgets.push({ id: "start_trend", position: widgetOrder.startTrend });

  return widgets.sort(({ position: left }, { position: right }) => left - right);
};

const Widget = ({ widget }) => {
  switch (widget.id) {
    case "recognition":
      return <RecognitionWidget />;
    case "survey":
      return <SurveyWidget />;
    case "leaderboard":
      return <LeaderboardWidget />;
    case "start_trend":
      return <StartTrendWidget />;
    default:
      return <WidgetRow widget={widget} />;
  }
};

const WidgetList = () => {
  const { profile, refreshProfile } = useProfile();

  const [page, onPageClick] = usePagination();
  const { error, json: { announcements = [], pagination = {} }, loading } = useFetch(
    () => client.listAnnouncements({ page }),
    [page]
  );

  const sortableRef = useSortable({
    handle: ".sortable__drag-handler",
    onSort: async event => {
      const order = Array.from(event.to.children).map(widget => (
        widget.getAttribute("data-id")
      ));

      await client.reorderWidgets({ order });
      refreshProfile();
    }
  });

  const widgets = useMemo(
    () => getWidgets(announcements, profile.organization),
    [announcements, profile]
  );

  return (
    <>
      <PageHeader title={t("Manage Widgets")}>
        <Trans>
          Create widgets to display featured content to certain groups. Sort the
          order of widgets to determine which ones display on top.
        </Trans>
      </PageHeader>

      <Anchor to="/admin/widgets/create" icon="plus" primary>
        <Trans>Add a New Widget</Trans>
      </Anchor>
      <br /><br />

      {error && <ErrorMessage error={error} />}

      <Loader loading={loading}>
        <Table>
          <thead>
            <tr>
              <th />
              <th />
              <th>
                <Trans>Created</Trans>
              </th>
              <th>
                <Trans>Header</Trans>
              </th>
              <th>
                <Trans>Message</Trans>
              </th>
              <th>
                <Trans>Display</Trans>
              </th>
              <th className="pull-right">
                <Trans>Actions</Trans>
              </th>
            </tr>
          </thead>
          <tbody ref={sortableRef}>
            {widgets.map(widget => (
              <Widget key={widget.id} widget={widget} />
            ))}
          </tbody>
        </Table>

        {widgets.length === 0 && <NoWidgets />}
        <Pagination {...pagination} onClick={onPageClick} />
      </Loader>
    </>
  );
};

export default WidgetList;
